import React, { useState, useRef } from "react";
import styled from "styled-components";
import { Lock } from "lucide-react";
import { useQueryParams, StringParam, NumberParam } from "use-query-params";
import axios from "axios";
import { Spin, Tooltip } from "antd";

import Globais from "../../components/Globais";
import { useMessage } from "../../uteis/mensagens/useMessage";

const Container = styled.div`
  width: 100%;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormWrapper = styled.div`
  background: #fff;
  width: 100%;
  max-width: 420px;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.4s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(15px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Title = styled.h2`
  text-align: center;
  color: #003644;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1.1rem;

  svg {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    color: #777;
  }

  input {
    width: 100%;
    padding: 0.8rem 0.8rem 0.8rem 2.5rem;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    transition: border 0.2s, box-shadow 0.2s;

    &:focus {
      border-color: #0096c7;
      box-shadow: 0 0 0 2px rgba(0, 150, 199, 0.2);
    }
  }
`;

const Botao = styled.button`
  width: 100%;
  background: #0096c7;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.9rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 0.6rem;
  transition: background 0.3s;

  &:hover {
    background: #00b4d8;
  }

  &:disabled {
    background: #90e0ef;
    cursor: not-allowed;
  }
`;

export default function ViewRedefinirSenha() {
  const [formData, setFormData] = useState({ password: "", confirmSenha: "" });
  const [isLoading, setIsLoading] = useState(false);
  const aguardando = useRef(false);

  const { password, confirmSenha } = formData;
  const [ntoken] = useQueryParams({ token: StringParam });
  const [nid] = useQueryParams({ id: NumberParam });
  const { showSuccess, showError, showWarning, contextHolder } = useMessage();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    aguardando.current = true;

    const token = ntoken.token;
    const id = nid.id;

    const passwordValidator = require("password-validator");
    const schema = new passwordValidator()
      .is()
      .min(8)
      .has()
      .uppercase()
      .has()
      .lowercase()
      .has()
      .digits(1)
      .has()
      .not()
      .spaces();

    if (!window.navigator.onLine) {
      showWarning("Sem conexão com a internet!");
      return;
    }

    if (password !== confirmSenha) {
      showError("As senhas não coincidem!");
      return;
    }

    if (!schema.validate(password)) {
      const erros = schema.validate(password, { list: true });
      const mensagens: Record<string, string> = {
        min: "A senha deve ter no mínimo 8 caracteres!",
        uppercase: "A senha deve conter ao menos uma letra maiúscula!",
        lowercase: "A senha deve conter ao menos uma letra minúscula!",
        digits: "A senha deve conter ao menos um número!",
        spaces: "A senha não deve conter espaços!",
      };
      showWarning(mensagens[erros[0]] || "Senha inválida.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(`${Globais.DOMINIOBACK}/auth/redefinirsenha`, {
        password,
        token,
        id,
      });

      if (response) {
        showSuccess("✅ Senha redefinida com sucesso!");
        setTimeout(() => (document.location.href = "/login"), 2000);
      }
    } catch (error: any) {
      console.error("Erro:", error);
      const code = error.response?.data?.code;
      const msg = error.response?.data?.error;

      switch (code) {
        case "TOKEN_INVALIDO":
          showError("O link de redefinição é inválido ou expirou.");
          break;
        default:
          showError(msg || "❌ Ocorreu um erro ao redefinir a senha.");
      }
    } finally {
      aguardando.current = false;
      setIsLoading(false);
    }
  };

  return (
    <Container>
      {contextHolder}
      <FormWrapper>
        <form onSubmit={handleSubmit}>
          <Title>Redefinir Senha</Title>

          <InputGroup>
            <Lock size={18} />
            <Tooltip title="A senha deve conter letras maiúsculas, minúsculas e ao menos um número.">
              <input
                type="password"
                id="password"
                required
                placeholder="Nova senha"
                value={password}
                onChange={handleChange}
              />
            </Tooltip>
          </InputGroup>

          <InputGroup>
            <Lock size={18} />
            <input
              type="password"
              id="confirmSenha"
              required
              placeholder="Confirmar nova senha"
              value={confirmSenha}
              onChange={handleChange}
            />
          </InputGroup>

          <Botao type="submit" disabled={isLoading}>
            {isLoading ? <Spin size="small" /> : "Redefinir Senha"}
          </Botao>
        </form>
      </FormWrapper>
    </Container>
  );
}
